// src/components/RouteTour.jsx
import React, { useEffect, useState } from 'react';
import Joyride from 'react-joyride';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguageSwitch';
import useRouteTour from '../../hooks/useRouteTour';
import { ROUTE_TOURS } from '../../configs/tourSteps';

export default function RouteTour() {
  const location = useLocation();
  const langContext = useLanguage();
  const lang = langContext?.selectedLang || 'en-US';
  const [isReady, setIsReady] = useState(false);

  // normalize route key
  const path = location.pathname;
  // try direct match first, otherwise check prefixes (like /application/:id)
  let routeKey = Object.keys(ROUTE_TOURS).find((k) => k === path);

  if (!routeKey) {
    // match application/:id
    if (path.startsWith('/application')) routeKey = '/application';
    else if (path.startsWith('/admin')) {
      // admin dashboard routes
      if (path.startsWith('/admin/analyticsDetails')) routeKey = '/analyticsDetails';
      else if (path.startsWith('/admin/analytics')) routeKey = '/analytics';
      else routeKey = null;
    } else {
      // try path without trailing slashes
      const simple = '/' + path.split('/')[1];
      routeKey = simple && Object.keys(ROUTE_TOURS).includes(simple) ? simple : null;
    }
  }

  const steps = routeKey && ROUTE_TOURS[routeKey] ? ROUTE_TOURS[routeKey](lang) || [] : [];
  const { run, stepIndex, callback, restart } = useRouteTour(routeKey, steps);

  // Wait for DOM elements to be ready before starting tour
  useEffect(() => {
    if (!steps.length || !run) {
      setIsReady(false);
      return;
    }

    // Reset ready state when route or run changes
    setIsReady(false);

    const checkDOMReady = () => {
      // Check if all target elements exist in the DOM
      const allTargetsExist = steps.every((step) => {
        try {
          const element = document.querySelector(step.target);
          return element !== null;
        } catch (error) {
          console.warn(`Invalid selector: ${step.target}`, error);
          return false;
        }
      });

      if (allTargetsExist) {
        setIsReady(true);
        return true;
      }
      return false;
    };

    // Initial check - elements might already be in DOM
    if (checkDOMReady()) return;

    // If not ready, wait a bit for React to render
    const initialTimeout = setTimeout(() => {
      if (checkDOMReady()) return;

      // Set up MutationObserver to watch for DOM changes
      const observer = new MutationObserver(() => {
        if (checkDOMReady()) {
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Fallback: start tour after max wait time even if elements not found
      const maxWaitTimeout = setTimeout(() => {
        observer.disconnect();
        console.warn('Tour starting after timeout - some elements may not be found');
        setIsReady(true);
      }, 5000);

      // Cleanup function
      return () => {
        observer.disconnect();
        clearTimeout(maxWaitTimeout);
      };
    }, 300); // Wait 300ms for initial render

    return () => {
      clearTimeout(initialTimeout);
    };
  }, [steps, run, routeKey, path]);

  if (!routeKey || !steps.length) return null;
  if (!isReady) return null;

  return (
    <>
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        scrollToFirstStep
        scrollOffset={100}
        spotlightPadding={12}
        callback={callback}
        disableScrolling={false}
        disableScrollParentFix={false}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip Tour',
        }}
        styles={{
          options: {
            zIndex: 999,
            arrowColor: '#ffffff',
          },
          buttonNext: {
            backgroundColor: '#2563eb',
            borderRadius: '8px',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#ffffff',
            border: 'none',
            outline: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          },
          buttonBack: {
            color: '#ffffff',
            marginRight: '12px',
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: '500',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: '#6e6d6d',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          },
          buttonClose: {
            color: '#ed141f',
          },
          buttonSkip: {
            color: '#ed141f',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          },
          spotlight: {
            borderRadius: '8px',
            padding: '10px',
          },
          tooltip: {
            borderRadius: '12px',
            backgroundColor: '#ffffff',
            maxWidth: '380px',
          },
          tooltipContainer: {
            textAlign: 'left',
            padding: '14px',
          },
          tooltipContent: {
            padding: 0,
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#282828',
          },
        }}
      />
    </>
  );
}
